/* GrowthOS — Notes (shared across all module view pages).
   Quick-capture composer + editable note cards, ported from design growthos/Notes.
   Wired to the real note APIs (createNote / updateNote / deleteNote) and the redux
   notes list on `leadLocalData.notesList`.
   NOTE: the design's category picker + pin are omitted — the note backend has no
   such fields (only title / description / createdBy / updatedDateTime). */
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../../services/service';
import { fetchNotesAndAppendToModule } from '../../../redux/actions';
import { DetailDateFormatter, NameFormatter } from '../../../shared/controls/formatters';

const ICONS = {
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
  trash: <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" />,
  calendar: (<><rect x="3" y="4.5" width="18" height="16.5" rx="2.5" /><path d="M16 2.5v4M8 2.5v4M3 9.5h18" /></>),
  send: <path d="M7 17 17 7M7 7h10v10" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const noteDate = (n) => n?.updatedDateTime || n?.createdDateTime || n?.createdDate || null;

function Composer({ onAdd, busy }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const taRef = useRef(null);
  const canSave = !!(body.trim() || title.trim());

  const submit = () => {
    if (!canSave || busy) return;
    onAdd(title.trim() || 'Untitled note', body.trim());
    setTitle('');
    setBody('');
    if (taRef.current) taRef.current.style.height = 'auto';
  };

  return (
    <div className="gos-note-composer">
      <input
        className="gos-note-title-input"
        placeholder="Add a title…"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="gos-note-body-wrap">
        <textarea
          ref={taRef}
          className="gos-note-body-input"
          placeholder="Write a note…  ⌘↵ to save"
          value={body}
          rows={2}
          onChange={(e) => {
            setBody(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          }}
        />
        <div className="gos-note-composer-foot">
          <span className="gos-note-hint">Press ⌘↵ to save</span>
          <button type="button" className="gos-note-send" onClick={submit} disabled={!canSave || busy} title="Save note">
            {busy ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <Ic name="send" size={17} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteCard({ note, onSave, onDelete, busy }) {
  const [editing, setEditing] = useState(false);
  const [t, setT] = useState(note.title || '');
  const [b, setB] = useState(note.description || '');

  if (editing) {
    return (
      <div className="gos-note-card editing">
        <input className="gos-note-title-input" value={t} onChange={(e) => setT(e.target.value)} autoFocus />
        <textarea className="gos-note-body-input" value={b} rows={3} onChange={(e) => setB(e.target.value)} style={{ marginTop: 6 }} />
        <div className="gos-note-edit-actions">
          <button type="button" className="gos-btn gos-btn-ghost gos-btn-sm" onClick={() => { setT(note.title || ''); setB(note.description || ''); setEditing(false); }}>
            Cancel
          </button>
          <button
            type="button"
            className="gos-btn gos-btn-primary gos-btn-sm"
            disabled={busy}
            onClick={() => onSave(note, t.trim() || 'Untitled note', b.trim(), () => setEditing(false))}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gos-note-card">
      <div className="gos-note-card-head">
        <div className="gos-note-card-title">{note.title || 'Untitled note'}</div>
        <div className="gos-note-actions">
          <button type="button" className="gos-note-ic" title="Edit" onClick={() => setEditing(true)}>
            <Ic name="edit" size={14} />
          </button>
          <button type="button" className="gos-note-ic danger" title="Delete" onClick={() => onDelete(note)} disabled={busy}>
            <Ic name="trash" size={14} />
          </button>
        </div>
      </div>
      {note.description ? <div className="gos-note-card-body">{note.description}</div> : null}
      <div className="gos-note-card-foot">
        {noteDate(note) ? (
          <span className="gos-lr-chip gos-note-datechip">
            <Ic name="calendar" size={13} /> {DetailDateFormatter(noteDate(note))}
          </span>
        ) : null}
        {note.createdBy ? (
          <span className="gos-note-author">
            by <strong>{NameFormatter(note.createdBy)}</strong>
          </span>
        ) : null}
      </div>
    </div>
  );
}

const Notes = ({ leadLocalData, module = 'Lead' }) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const moduleId = leadLocalData?.id;

  const list = leadLocalData?.notesList;
  const ordered = Array.isArray(list)
    ? [...list].sort((a, b) => new Date(noteDate(b) || 0) - new Date(noteDate(a) || 0))
    : list;

  const refresh = () => dispatch(fetchNotesAndAppendToModule({ moduleName: module, id: moduleId }));

  const handleErrors = (response) => {
    const msgs = [];
    for (const key in response.errors) {
      if (response.errors.hasOwnProperty(key)) msgs.push(...response.errors[key]);
    }
    msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
  };

  const add = (title, description) => {
    if (busy) return;
    setBusy(true);
    apiServices
      .createNote({ title, description, type: 'add', moduleId, module })
      .then((response) => {
        if (response.status === 200) {
          refresh();
          toast.success('Note added successfully!', { position: 'top-right' });
        } else handleErrors(response);
      })
      .catch(() => toast.error('An unexpected error occurred.', { position: 'top-right' }))
      .finally(() => setBusy(false));
  };

  const save = (note, title, description, done) => {
    if (busy) return;
    setBusy(true);
    apiServices
      .updateNote({ ...note, title, description, type: 'edit', moduleId, module })
      .then((response) => {
        if (response.status === 200) {
          if (done) done();
          refresh();
          toast.success('Note updated successfully!', { position: 'top-right' });
        } else handleErrors(response);
      })
      .catch(() => toast.error('An unexpected error occurred.', { position: 'top-right' }))
      .finally(() => setBusy(false));
  };

  const remove = (note) => {
    if (busy) return;
    setBusy(true);
    apiServices
      .deleteNote({ id: note.id, module, moduleId })
      .then((response) => {
        if (response.status === 200) {
          refresh();
          toast.success('Note deleted successfully!', { position: 'top-right' });
        } else handleErrors(response);
      })
      .catch(() => toast.error('An unexpected error occurred.', { position: 'top-right' }))
      .finally(() => setBusy(false));
  };

  return (
    <div className="gos-lr-card gos-note-wrap">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="note" size={16} /> Notes
          {Array.isArray(list) && list.length > 0 ? <span className="gos-lr-count">{list.length}</span> : null}
        </div>
        <span className="gos-note-subhint">Newest first · ⌘↵ to save</span>
      </div>

      <div className="gos-note-body">
        <Composer onAdd={add} busy={busy} />
        <div className="gos-note-list">
          {!Array.isArray(list) ? (
            <div className="gos-note-empty">Loading notes…</div>
          ) : ordered.length === 0 ? (
            <div className="gos-note-empty">No notes yet — capture your first one above.</div>
          ) : (
            ordered.map((n, i) => (
              <NoteCard key={n.id || i} note={n} onSave={save} onDelete={remove} busy={busy} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Notes;
